import { useEffect, useState, type ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  V2Tabs,
  Icon,
} from '@sakhtar/design-system/react';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../../../lib/apiClient.js';
import { displayJalaliDate } from '../../../lib/date/displayJalali.js';
import { faDigits } from '../../../lib/faDigits.js';
import './documents.css';

export interface DocumentSummary {
  readonly documentId: string;
  readonly title: string;
  readonly sourceRef: string;
  readonly docType: string;
  readonly domain: string;
  readonly status: string;
  readonly docVersion?: string | undefined;
  readonly accessLevel?: 'all' | 'manager' | 'confidential' | undefined;
  readonly effectiveDate?: string | null | undefined;
  readonly chunksCount: number;
  readonly createdAt: string;
}

export interface DocumentChunkView {
  readonly chunkId: string;
  readonly chunkIndex: number;
  readonly content: string;
  readonly tokenCount: number;
  readonly createdAt: string;
}

export interface RetrievedChunk {
  readonly chunkId: string;
  readonly documentId: string;
  readonly title: string;
  readonly sourceRef: string;
  readonly docType: string;
  readonly domain: string;
  readonly content: string;
  readonly score: number;
  readonly denseScore?: number | undefined;
  readonly sparseScore?: number | undefined;
}

export interface CompanyProfile {
  companyName: string;
  industry: string;
  missionVision: string;
  coreProductsServices: string;
  targetAudience: string;
  salesPaymentTerms: string;
  returnWarrantyPolicy: string;
  competitiveAdvantage: string;
  communicationTone: string;
  redLinesAndGuardrails: string;
  updatedAt?: string | undefined;
}

const DOMAIN_LABELS: Record<string, string> = {
  inventory: 'انبار و موجودی',
  sales: 'فروش و مشتریان',
  procurement: 'خرید و تدارکات',
  finance: 'مالی و حسابداری',
  projects: 'پروژه‌ها و وظایف',
  sakhtar: 'سازمانی و عمومی',
};

const DOCTYPE_LABELS: Record<string, string> = {
  policy: 'آیین‌نامه و خط‌مشی',
  manual: 'راهنما و استاندارد',
  contract: 'قرارداد و پیمان',
  statement: 'صورت‌جلسه',
  report: 'گزارش تحلیلی',
  general: 'عمومی',
};

const ACCESS_LABELS: Record<string, string> = {
  all: 'همه کارکنان',
  manager: 'سطح مدیران',
  confidential: 'محرمانه',
};

export function DocumentsPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'questionnaire' | 'list' | 'upload' | 'search'>('list');

  // Documents list state
  const [documents, setDocuments] = useState<readonly DocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Questionnaire state
  const [profile, setProfile] = useState<CompanyProfile>({
    companyName: '',
    industry: '',
    missionVision: '',
    coreProductsServices: '',
    targetAudience: '',
    salesPaymentTerms: '',
    returnWarrantyPolicy: '',
    competitiveAdvantage: '',
    communicationTone: '',
    redLinesAndGuardrails: '',
  });
  const [completion, setCompletion] = useState(0);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileNotice, setProfileNotice] = useState<string | null>(null);

  // Upload & Process Workflow State
  const [uploadStep, setUploadStep] = useState<'input' | 'preview'>('input');
  const [title, setTitle] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [domain, setDomain] = useState('sakhtar');
  const [docType, setDocType] = useState('policy');
  const [docVersion, setDocVersion] = useState('1.0');
  const [accessLevel, setAccessLevel] = useState<'all' | 'manager' | 'confidential'>('all');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [uploadMode, setUploadMode] = useState<'file' | 'text'>('file');
  const [rawContent, setRawContent] = useState('');
  const [selectedFile, setSelectedFile] = useState<{
    filename: string;
    mimeType: string;
    base64: string;
  } | null>(null);
  const [parsedMarkdown, setParsedMarkdown] = useState('');
  const [estimatedTokens, setEstimatedTokens] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<readonly RetrievedChunk[]>([]);
  const [searching, setSearching] = useState(false);

  // Chunk inspection state
  const [viewingDoc, setViewingDoc] = useState<DocumentSummary | null>(null);
  const [chunks, setChunks] = useState<readonly DocumentChunkView[]>([]);
  const [loadingChunks, setLoadingChunks] = useState(false);

  const fetchDocs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<{ documents: readonly DocumentSummary[]; total: number }>(
        `/t/${slug}/api/v1/ai/documents`,
      );
      setDocuments(res.documents ?? []);
    } catch {
      setError('خطا در دریافت فهرست اسناد پایگاه دانش.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    setLoadingProfile(true);
    try {
      const res = await apiGet<{ profile: CompanyProfile; completionPercentage: number }>(
        `/t/${slug}/api/v1/ai/company-profile`,
      );
      if (res.profile) {
        setProfile(res.profile);
        setCompletion(res.completionPercentage ?? 0);
      }
    } catch {
      // Non-blocking
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    void fetchDocs();
    void fetchProfile();
  }, [slug]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileNotice(null);
    try {
      const res = await apiPut<{ saved: boolean; completionPercentage: number }>(
        `/t/${slug}/api/v1/ai/company-profile`,
        profile,
      );
      setCompletion(res.completionPercentage ?? 0);
      setProfileNotice('شناخت‌نامه شرکت با موفقیت ذخیره و در پایگاه دانش دستیار ایندکس شد.');
      void fetchDocs();
    } catch {
      alert('خطا در ذخیره شناخت‌نامه شرکت.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleToggleStatus = async (doc: DocumentSummary) => {
    const nextStatus = doc.status === 'active' ? 'inactive' : 'active';
    try {
      await apiPatch(`/t/${slug}/api/v1/ai/documents/${doc.documentId}/status`, {
        status: nextStatus,
      });
      setDocuments((prev) =>
        prev.map((d) => (d.documentId === doc.documentId ? { ...d, status: nextStatus } : d)),
      );
    } catch {
      alert('خطا در تغییر وضعیت سند.');
    }
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('آیا از حذف این سند و ابطال تمام چانک‌های آن در هوش مصنوعی اطمینان دارید؟')) {
      return;
    }
    try {
      await apiDelete(`/t/${slug}/api/v1/ai/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.documentId !== docId));
    } catch {
      alert('خطا در حذف سند.');
    }
  };

  const handleViewChunks = async (doc: DocumentSummary) => {
    setViewingDoc(doc);
    setLoadingChunks(true);
    try {
      const res = await apiGet<readonly DocumentChunkView[]>(
        `/t/${slug}/api/v1/ai/documents/${doc.documentId}/chunks`,
      );
      setChunks(res ?? []);
    } catch {
      alert('خطا در دریافت بندهای سند.');
    } finally {
      setLoadingChunks(false);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const raw = reader.result as string;
      const base64 = raw.split(',')[1] ?? '';
      setSelectedFile({
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        base64,
      });
      if (!title) {
        setTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    };
    reader.readAsDataURL(file);
  };

  // Step 1 -> Step 2: Parse to Markdown
  const handleProcessToMarkdown = async (e: React.FormEvent) => {
    e.preventDefault();
    if (uploadMode === 'file' && !selectedFile) {
      alert('لطفاً یک فایل (PDF، Word یا متنی) انتخاب کنید.');
      return;
    }
    if (uploadMode === 'text' && !rawContent.trim()) {
      alert('لطفاً متن سند را وارد کنید.');
      return;
    }

    setProcessing(true);
    try {
      if (uploadMode === 'file' && selectedFile) {
        const res = await apiPost<{ markdown: string; tokenCount: number }>(
          `/t/${slug}/api/v1/ai/documents/parse`,
          {
            contentBase64: selectedFile.base64,
            filename: selectedFile.filename,
            mimeType: selectedFile.mimeType,
          },
        );
        setParsedMarkdown(res.markdown);
        setEstimatedTokens(res.tokenCount);
      } else {
        setParsedMarkdown(rawContent.trim());
        setEstimatedTokens(Math.ceil(rawContent.trim().length / 2));
      }
      setUploadStep('preview');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'خطا در استخراج و تبدیل فایل به Markdown.';
      alert(msg);
    } finally {
      setProcessing(false);
    }
  };

  // Step 2 -> Index: Approve & Add to Knowledge
  const handleApproveAndIndex = async () => {
    if (!title.trim() || !sourceRef.trim()) {
      alert('عنوان و شناسه منبع سند الزامی هستند.');
      return;
    }
    if (!parsedMarkdown.trim()) {
      alert('متن Markdown سند نمی‌تواند خالی باشد.');
      return;
    }

    setSubmitting(true);
    setUploadSuccess(null);
    try {
      const res = await apiPost<{ documentId: string; chunksCount: number }>(
        `/t/${slug}/api/v1/ai/documents`,
        {
          title: title.trim(),
          sourceRef: sourceRef.trim(),
          domain,
          docType,
          docVersion: docVersion.trim() || '1.0',
          accessLevel,
          effectiveDate: effectiveDate.trim() || undefined,
          content: parsedMarkdown.trim(),
        },
      );

      setUploadSuccess(
        `سند نسخه ${faDigits(docVersion)} با موفقیت تأیید و در پایگاه دانش ایندکس شد (${faDigits(res.chunksCount)} بخش متنی ایجاد شد).`,
      );
      setUploadStep('input');
      setTitle('');
      setSourceRef('');
      setRawContent('');
      setParsedMarkdown('');
      setSelectedFile(null);
      void fetchDocs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'خطا در نمایه‌سازی سند.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const res = await apiPost<{ chunks: readonly RetrievedChunk[] }>(
        `/t/${slug}/api/v1/ai/documents/search`,
        { query: searchQuery.trim(), limit: 5 },
      );
      setSearchResults(res.chunks ?? []);
    } catch {
      alert('خطا در اجرای جستجوی معنایی.');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="ai-docs">
      <div className="ai-docs__toolbar">
        <V2Tabs
          items={[
            { id: 'questionnaire', label: 'شناخت شرکت (پرسشنامه مدیر)' },
            { id: 'list', label: 'فهرست اسناد تأییدشده' },
            { id: 'upload', label: 'بارگذاری و پردازش سند' },
            { id: 'search', label: 'کاوشگر معنایی / آزمون RAG' },
          ]}
          activeId={activeTab}
          onChange={(id) =>
            setActiveTab(id as 'questionnaire' | 'list' | 'upload' | 'search')
          }
        />
      </div>

      {activeTab === 'questionnaire' && (
        <V2Card>
          <V2CardHeader
            title="پرسشنامه شناخت شرکت و خط‌مشی‌های مدیریتی"
            subtitle="این اطلاعات به عنوان بستر بنیادین شناخت سازمان در حافظه پایگاه دانش ذخیره شده و به همه یارهای هوش مصنوعی جهت درک زمینه کسب‌وکار تزریق می‌شود. ارقام قطعی سیستم کماکان از پایگاه داده ERP خوانده می‌شوند."
          />
          {loadingProfile ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--sk-text-muted)' }}>
              در حال بارگذاری پرسشنامه شناخت شرکت...
            </div>
          ) : (
            <form onSubmit={handleSaveProfile} style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="ai-docs__progress-container">
                <span style={{ fontSize: '13px', fontWeight: 600 }}>میزان تکمیل شناخت‌نامه:</span>
                <div className="ai-docs__progress-bar">
                  <div
                    className="ai-docs__progress-fill"
                    style={{ width: `${Math.min(Math.max(completion, 0), 100)}%` }}
                  />
                </div>
                <V2Badge tone={completion >= 80 ? 'success' : 'brand'}>
                  {faDigits(completion)}٪ تکمیل‌شده
                </V2Badge>
              </div>

              {profileNotice && (
                <div
                  style={{
                    background: 'var(--sk-success-50)',
                    color: 'var(--sk-success-800)',
                    padding: '12px',
                    borderRadius: '6px',
                  }}
                >
                  {profileNotice}
                </div>
              )}

              {/* Section 1 */}
              <div className="ai-docs__section">
                <h4 className="ai-docs__section-title">۱. هویت، رسالت و ماهیت کسب‌وکار</h4>
                <div className="ai-docs__form-row">
                  <div className="ai-docs__form-field">
                    <label>نام رسمی یا تجاری شرکت</label>
                    <input
                      type="text"
                      placeholder="مثال: شرکت بازرگانی ساختار نوین"
                      value={profile.companyName}
                      onChange={(e) => setProfile({ ...profile, companyName: e.target.value })}
                    />
                  </div>
                  <div className="ai-docs__form-field">
                    <label>صنعت و حوزه فعالیت</label>
                    <input
                      type="text"
                      placeholder="مثال: تأمین و توزیع تجهیزات الکترونیک و قطعات"
                      value={profile.industry}
                      onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
                    />
                  </div>
                </div>
                <div className="ai-docs__form-field">
                  <label>مأموریت و چشم‌انداز محوری</label>
                  <textarea
                    rows={2}
                    placeholder="مأموریت کلیدی سازمان و ارزشی که برای بازار خلق می‌کند..."
                    value={profile.missionVision}
                    onChange={(e) => setProfile({ ...profile, missionVision: e.target.value })}
                  />
                </div>
              </div>

              {/* Section 2 */}
              <div className="ai-docs__section">
                <h4 className="ai-docs__section-title">۲. سبد محصولات، خدمات و بازار هدف</h4>
                <div className="ai-docs__form-field">
                  <label>کالاها و خدمات اصلی</label>
                  <textarea
                    rows={2}
                    placeholder="فهرست و توصیف دسته‌بندی‌های اصلی کالا یا خدماتی که سازمان ارائه می‌دهد..."
                    value={profile.coreProductsServices}
                    onChange={(e) =>
                      setProfile({ ...profile, coreProductsServices: e.target.value })
                    }
                  />
                </div>
                <div className="ai-docs__form-field">
                  <label>مشتریان و پرسونای هدف</label>
                  <input
                    type="text"
                    placeholder="مثال: خریداران عمده استانی، فروشگاه‌های زنجیره‌ای و سازمان‌های دولتی"
                    value={profile.targetAudience}
                    onChange={(e) => setProfile({ ...profile, targetAudience: e.target.value })}
                  />
                </div>
              </div>

              {/* Section 3 */}
              <div className="ai-docs__section">
                <h4 className="ai-docs__section-title">۳. سیاست‌های تجاری، فروش، مرجوعی و تسویه</h4>
                <div className="ai-docs__form-field">
                  <label>شرایط و سیاست‌های فروش، تخفیف و تسویه حساب</label>
                  <textarea
                    rows={3}
                    placeholder="مثال: فروش نقدی با ۲٪ تخفیف؛ فروش اعتباری صرفاً با چک صیادی تا سقف ۴۵ روز پس از اعتبارسنجی..."
                    value={profile.salesPaymentTerms}
                    onChange={(e) =>
                      setProfile({ ...profile, salesPaymentTerms: e.target.value })
                    }
                  />
                </div>
                <div className="ai-docs__form-field">
                  <label>قوانین بازگشت کالا (مرجوعی) و گارانتی</label>
                  <textarea
                    rows={2}
                    placeholder="شرایط پذیرش مرجوعی، مهلت بررسی و استانداردهای ضمانت کالا..."
                    value={profile.returnWarrantyPolicy}
                    onChange={(e) =>
                      setProfile({ ...profile, returnWarrantyPolicy: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Section 4 */}
              <div className="ai-docs__section">
                <h4 className="ai-docs__section-title">۴. مزیت رقابتی، لحن سازمانی و خطوط قرمز</h4>
                <div className="ai-docs__form-row">
                  <div className="ai-docs__form-field">
                    <label>مزیت متمایزکننده رقابتی</label>
                    <input
                      type="text"
                      placeholder="مثال: تحویل ۲۴ ساعته کالا، تضمین اصالت و مشاوره فنی تخصصی"
                      value={profile.competitiveAdvantage}
                      onChange={(e) =>
                        setProfile({ ...profile, competitiveAdvantage: e.target.value })
                      }
                    />
                  </div>
                  <div className="ai-docs__form-field">
                    <label>لحن ارتباطی دستیار هوشمند با پرسنل و مشتریان</label>
                    <input
                      type="text"
                      placeholder="مثال: رسمی، دقیق، تحلیل‌گرایانه و محترمانه بدون اغراق"
                      value={profile.communicationTone}
                      onChange={(e) =>
                        setProfile({ ...profile, communicationTone: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="ai-docs__form-field">
                  <label>خطوط قرمز و ممنوعیت‌های سازمانی در پاسخ‌ها</label>
                  <textarea
                    rows={2}
                    placeholder="مثال: هرگز حاشیه سود یا تأمین‌کننده اصلی برای مشتری افشا نشود؛ اطلاعات مالی محرمانه صرفاً به مدیران مجاز ارائه شود."
                    value={profile.redLinesAndGuardrails}
                    onChange={(e) =>
                      setProfile({ ...profile, redLinesAndGuardrails: e.target.value })
                    }
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <V2Button variant="primary" type="submit" disabled={savingProfile}>
                  {savingProfile ? 'در حال ذخیره و ایندکس...' : 'ذخیره و همگام‌سازی با پایگاه دانش'}
                </V2Button>
              </div>
            </form>
          )}
        </V2Card>
      )}

      {activeTab === 'list' && (
        <V2Card>
          <V2CardHeader
            title="پایگاه اسناد و خط‌مشی‌های تأییدشده سازمان (RAG Knowledge Base)"
            subtitle="اسناد فعال در این بخش به عنوان منبع دانش رسمی، مکمل داده‌های ثبتی ERP بوده و بر تحلیل‌ها و پاسخ‌های هوش مصنوعی اثر می‌گذارند."
          />
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--sk-text-muted)' }}>
              در حال بارگذاری اسناد...
            </div>
          ) : error ? (
            <div style={{ padding: '24px', color: 'var(--sk-danger-700)' }}>{error}</div>
          ) : documents.length === 0 ? (
            <V2EmptyState
              title="هنوز سندی تأیید و نمایه‌سازی نشده است"
              description="برای شروع، خط‌مشی‌ها، دستورالعمل‌های انبار، شرایط فروش یا قراردادهای سازمان را بارگذاری و تأیید کنید."
              action={
                <V2Button variant="primary" onClick={() => setActiveTab('upload')}>
                  افزودن و پردازش اولین سند
                </V2Button>
              }
            />
          ) : (
            <div className="ai-docs__grid" style={{ padding: '16px' }}>
              {documents.map((doc) => (
                <div
                  key={doc.documentId}
                  className={`ai-doc-card ${doc.status !== 'active' ? 'ai-doc-card--inactive' : ''}`}
                >
                  <div className="ai-doc-card__header">
                    <div>
                      <h4 className="ai-doc-card__title">{doc.title}</h4>
                      <span className="ai-doc-card__ref">{doc.sourceRef}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <V2Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleStatus(doc)}
                        title={doc.status === 'active' ? 'غیرفعال‌سازی سند' : 'فعال‌سازی مجدد سند'}
                      >
                        <Icon name={doc.status === 'active' ? 'eyeOff' : 'eye'} size="sm" />
                      </V2Button>
                      <V2Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(doc.documentId)}
                        title="حذف سند و ابطال چانک‌ها"
                      >
                        <Icon name="x" size="sm" />
                      </V2Button>
                    </div>
                  </div>
                  <div className="ai-doc-card__badges">
                    <V2Badge tone="neutral">{DOMAIN_LABELS[doc.domain] ?? doc.domain}</V2Badge>
                    <V2Badge tone="info">{DOCTYPE_LABELS[doc.docType] ?? doc.docType}</V2Badge>
                    <V2Badge tone="brand">نسخه {faDigits(doc.docVersion ?? '1.0')}</V2Badge>
                    <V2Badge tone={doc.accessLevel === 'manager' || doc.accessLevel === 'confidential' ? 'warn' : 'neutral'}>
                      {ACCESS_LABELS[doc.accessLevel ?? 'all'] ?? doc.accessLevel}
                    </V2Badge>
                    <V2Badge tone={doc.status === 'active' ? 'success' : 'neutral'}>
                      {doc.status === 'active' ? 'فعال در دانش' : 'غیرفعال'}
                    </V2Badge>
                    <V2Badge tone="brand">{faDigits(doc.chunksCount)} بند (Chunk)</V2Badge>
                  </div>
                  <div className="ai-doc-card__footer">
                    <span>
                      {doc.effectiveDate
                        ? `تاریخ اجرا: ${doc.effectiveDate}`
                        : `تاریخ ثبت: ${displayJalaliDate(doc.createdAt)}`}
                    </span>
                    <V2Button variant="secondary" size="sm" onClick={() => handleViewChunks(doc)}>
                      مشاهده بندها ({faDigits(doc.chunksCount)})
                    </V2Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </V2Card>
      )}

      {activeTab === 'upload' && (
        <V2Card>
          <V2CardHeader
            title="فرآیند بارگذاری، پردازش و تأیید اسناد سازمانی"
            subtitle="مرحله اول: بارگذاری فایل یا ورود متن → مرحله دوم: استخراج، پیش‌نمایش و ویرایش Markdown → مرحله سوم: تأیید نهایی و ایندکس برداری در دانش شرکت."
          />
          <div style={{ padding: '20px' }}>
            {uploadSuccess && (
              <div
                style={{
                  background: 'var(--sk-success-50)',
                  color: 'var(--sk-success-800)',
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '16px',
                }}
              >
                {uploadSuccess}
              </div>
            )}

            {uploadStep === 'input' ? (
              <form onSubmit={handleProcessToMarkdown} className="ai-docs__upload-card">
                <div style={{ display: 'flex', gap: '12px', margin: '4px 0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="uploadMode"
                      checked={uploadMode === 'file'}
                      onChange={() => setUploadMode('file')}
                    />
                    بارگذاری فایل (PDF، Word DOCX، Text، Markdown)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="uploadMode"
                      checked={uploadMode === 'text'}
                      onChange={() => setUploadMode('text')}
                    />
                    ورود مستقیم متن سند
                  </label>
                </div>

                {uploadMode === 'file' ? (
                  <div
                    className="ai-docs__dropzone"
                    onClick={() => document.getElementById('doc-file-input')?.click()}
                  >
                    <input
                      id="doc-file-input"
                      type="file"
                      accept=".pdf,.docx,.xlsx,.txt,.md,.csv,.json,.png,.jpg,.jpeg,.webp"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <Icon name="upload" size="md" />
                    {selectedFile ? (
                      <div>
                        <strong>فایل انتخاب‌شده:</strong> {selectedFile.filename}
                      </div>
                    ) : (
                      <div>
                        برای انتخاب فایل کلیک کنید یا فایل را بکشید و رها کنید (پشتیبانی استاندارد از Excel، Word، PDF، تصاویر و متنی)
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="ai-docs__form-field">
                    <label>متن اولیه سند</label>
                    <textarea
                      rows={8}
                      placeholder="متن مصوبه، آیین‌نامه یا شرایط قرارداد را اینجا بنویسید یا جای‌گذاری کنید..."
                      value={rawContent}
                      onChange={(e) => setRawContent(e.target.value)}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                  <V2Button variant="primary" type="submit" disabled={processing}>
                    {processing ? 'در حال استخراج و تبدیل به Markdown...' : 'پردازش فایل و مشاهده پیش‌نمایش Markdown'}
                  </V2Button>
                </div>
              </form>
            ) : (
              /* Step 2: Markdown Preview, Edit, Governance, and Final Approval */
              <div className="ai-docs__upload-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700 }}>
                    پیش‌نمایش و ویرایش متن Markdown سند قبل از ذخیره در دانش شرکت
                  </h4>
                  <V2Badge tone="brand">تخمین توکن: ~{faDigits(estimatedTokens)}</V2Badge>
                </div>

                <div className="ai-docs__form-row">
                  <div className="ai-docs__form-field">
                    <label>عنوان سند *</label>
                    <input
                      type="text"
                      placeholder="مثال: آیین‌نامه انبارداری و تحویل کالا ۱۴۰۵"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                  </div>
                  <div className="ai-docs__form-field">
                    <label>شناسه منبع (کد مرجع) *</label>
                    <input
                      type="text"
                      placeholder="مثال: POL-INV-1405"
                      value={sourceRef}
                      onChange={(e) => setSourceRef(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="ai-docs__form-row">
                  <div className="ai-docs__form-field">
                    <label>حوزه داده (یار مرتبط)</label>
                    <select value={domain} onChange={(e) => setDomain(e.target.value)}>
                      <option value="sakhtar">سازمانی / عمومی (کل یارها)</option>
                      <option value="inventory">انبار و کالا (انبار یار)</option>
                      <option value="sales">فروش و سفارشات (فروش یار)</option>
                      <option value="procurement">خرید و تدارکات (تدارکات یار)</option>
                      <option value="finance">مالی و خزانه‌داری (مالی یار)</option>
                      <option value="projects">پروژه‌ها و فعالیت‌ها (پروژه یار)</option>
                    </select>
                  </div>
                  <div className="ai-docs__form-field">
                    <label>نوع سند</label>
                    <select value={docType} onChange={(e) => setDocType(e.target.value)}>
                      <option value="policy">آیین‌نامه و خط‌مشی</option>
                      <option value="manual">راهنما و دستورالعمل اجرایی</option>
                      <option value="contract">قرارداد و پیمان</option>
                      <option value="statement">صورت‌جلسه و توافق‌نامه</option>
                      <option value="report">گزارش و تحلیل</option>
                      <option value="general">عمومی</option>
                    </select>
                  </div>
                  <div className="ai-docs__form-field">
                    <label>نسخه سند</label>
                    <input
                      type="text"
                      placeholder="1.0"
                      value={docVersion}
                      onChange={(e) => setDocVersion(e.target.value)}
                    />
                  </div>
                  <div className="ai-docs__form-field">
                    <label>سطح دسترسی</label>
                    <select
                      value={accessLevel}
                      onChange={(e) =>
                        setAccessLevel(e.target.value as 'all' | 'manager' | 'confidential')
                      }
                    >
                      <option value="all">عمومی (همه کارکنان دارای مجوز)</option>
                      <option value="manager">مدیران سازمان</option>
                      <option value="confidential">محرمانه / حساس</option>
                    </select>
                  </div>
                </div>

                <div className="ai-docs__form-field">
                  <label>تاریخ مؤثر / تاریخ اجرا</label>
                  <input
                    type="text"
                    placeholder="مثال: ۱۴۰۵/۰۸/۰۱"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                  />
                </div>

                <div className="ai-docs__form-field">
                  <label>محتوای متنی قابل ویرایش (Markdown)</label>
                  <textarea
                    rows={12}
                    value={parsedMarkdown}
                    onChange={(e) => {
                      setParsedMarkdown(e.target.value);
                      setEstimatedTokens(Math.ceil(e.target.value.length / 2));
                    }}
                    style={{ fontFamily: 'monospace', fontSize: '12px', direction: 'rtl' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
                  <V2Button
                    variant="ghost"
                    onClick={() => setUploadStep('input')}
                    disabled={submitting}
                  >
                    بازگشت و اصلاح فایل
                  </V2Button>
                  <V2Button
                    variant="primary"
                    onClick={handleApproveAndIndex}
                    disabled={submitting || !parsedMarkdown.trim() || !title.trim() || !sourceRef.trim()}
                  >
                    {submitting ? 'در حال ایندکس و بردارسازی...' : 'تأیید نهایی و افزودن به پایگاه دانش شرکت'}
                  </V2Button>
                </div>
              </div>
            )}
          </div>
        </V2Card>
      )}

      {activeTab === 'search' && (
        <V2Card>
          <V2CardHeader
            title="کاوشگر معنایی و تست بازیابی هیبریدی (RAG Tester)"
            subtitle="پرسش یا عبارتی را وارد کنید تا بهترین بندهای منطبق با روش ترکیبی برداری و متنی (RRF) استخراج و رتبه‌بندی شوند."
          />
          <div style={{ padding: '16px' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px' }}>
              <div className="ai-docs__search-bar" style={{ flex: 1 }}>
                <Icon name="search" size="sm" />
                <input
                  type="text"
                  placeholder="مثلاً: سقف اختیارات خرید بدون تایید مدیر چیست یا سیاست مرجوعی کالا چگونه است؟"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <V2Button variant="primary" type="submit" disabled={searching}>
                {searching ? 'در حال جستجو...' : 'جستجو در اسناد'}
              </V2Button>
            </form>

            {searchResults.length > 0 && (
              <div className="ai-docs__search-results">
                <h4 style={{ fontSize: '13px', color: 'var(--sk-text-muted)' }}>
                  نتایج بازیابی‌شده هیبریدی ({faDigits(searchResults.length)} بند منطبق):
                </h4>
                {searchResults.map((chunk, idx) => (
                  <div key={chunk.chunkId || idx} className="ai-chunk-card">
                    <div className="ai-chunk-card__head">
                      <div>
                        <strong>{chunk.title}</strong> ({chunk.sourceRef})
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <V2Badge tone="brand">امتیاز RRF: {chunk.score.toFixed(4)}</V2Badge>
                        {chunk.denseScore !== undefined && (
                          <V2Badge tone="neutral">
                            معنایی: {(chunk.denseScore * 100).toFixed(0)}%
                          </V2Badge>
                        )}
                      </div>
                    </div>
                    <div className="ai-chunk-card__body">{chunk.content}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </V2Card>
      )}

      {viewingDoc && (
        <div className="ai-docs__modal-backdrop" onClick={() => setViewingDoc(null)}>
          <div className="ai-docs__modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ai-docs__modal-header">
              <div>
                <h3>{viewingDoc.title}</h3>
                <span className="ai-doc-card__ref">{viewingDoc.sourceRef}</span>
              </div>
              <V2Button variant="ghost" size="sm" onClick={() => setViewingDoc(null)}>
                <Icon name="x" size="sm" />
              </V2Button>
            </div>
            <div className="ai-docs__modal-body">
              {loadingChunks ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--sk-text-muted)' }}>
                  در حال دریافت بندهای نمایه‌شده...
                </div>
              ) : chunks.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--sk-text-muted)' }}>
                  بندی برای این سند یافت نشد.
                </div>
              ) : (
                chunks.map((ch) => (
                  <div key={ch.chunkId} className="ai-chunk-card">
                    <div className="ai-chunk-card__head">
                      <strong>بند شماره {faDigits(ch.chunkIndex + 1)}</strong>
                      <span>{faDigits(ch.tokenCount)} توکن</span>
                    </div>
                    <div className="ai-chunk-card__body">{ch.content}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
